/**
 * A worked example, set out as a model answer.
 *
 * The content arrives as one string with real newlines:
 *
 *   [R] Routine Level:
 *   Simplify 9^(3/2).
 *   Solution:
 *   Step 1: Write 9 as a prime power: 9 = 3²
 *   Step 2: Apply the power rule: ...
 *   ∴ 9^(3/2) = 27
 *
 * It used to go straight into <MathText>, which assigns innerHTML — so every
 * newline collapsed and the whole derivation ran together as one paragraph.
 * No amount of line-height fixed that; the structure had to be recovered
 * before it could be laid out. This parses the shape above and gives each
 * part its own element: a step label in the margin, the working beside it,
 * and the ∴ answer on its own line.
 *
 * Roughly half the 1,343 shipped examples carry no [K]/[R]/[C]/[P] tag and
 * many have no "Step N:" lines at all, so every part is optional and the
 * bare case — a problem and a one-line solution — still renders correctly.
 */
import { MathText } from "./Math";

/** CAPS cognitive levels. The tag is real curriculum taxonomy, not decoration. */
const LEVELS: Record<string, string> = {
  K: "Knowledge",
  R: "Routine",
  C: "Complex",
  P: "Problem-solving",
};

type Row = {
  /** Margin label — "Step 1", "Given", "∴". Absent for continuation lines. */
  label?: string;
  body: string;
  kind: "step" | "given" | "answer" | "plain";
};

/**
 * One question and its working. Some examples hold two or three of these —
 * "Write √x in exponential form. Solution: ... Write ³√(x⁵) ... Solution: ..."
 * — so a single problem/rows pair would fold the second question into the
 * first one's working, where it reads as a step rather than a new question.
 */
type Part = { problem: string[]; rows: Row[] };

type Parsed = {
  levelCode?: string;
  parts: Part[];
  notes: string[];
};

/**
 * A first line of "Routine Level:" only repeats the badge, so it is dropped.
 * A first line of "Simplify 3x..." is the problem itself and is kept.
 */
const LEVEL_LABEL_ONLY = /^(knowledge|routine|complex|problem[-\s]?solving)\s*level\s*:?\s*$/i;

export function parseWorkedExample(raw: string): Parsed {
  let text = raw.trim();
  let levelCode: string | undefined;

  const tag = text.match(/^\[([KRCP])\]\s*/i);
  if (tag) {
    levelCode = tag[1].toUpperCase();
    text = text.slice(tag[0].length);
  }

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const notes: string[] = [];
  const parts: Part[] = [];
  let current: Part = { problem: [], rows: [] };
  let inSolution = false;
  let inNote = false;

  const isSolutionLine = (l: string) => /^solution\s*:/i.test(l);

  lines.forEach((line, i) => {
    if (current.problem.length === 0 && !inSolution && LEVEL_LABEL_ONLY.test(line)) return;

    const note = line.match(/^note\s*:\s*(.*)$/i);
    if (note) {
      notes.push(note[1] || "");
      inNote = true;
      return;
    }

    // "Solution:" opens the working. Anything trailing it on the same line is
    // the first line of that working, not a heading.
    const solution = line.match(/^solution\s*:\s*(.*)$/i);
    if (solution) {
      inSolution = true;
      inNote = false;
      if (solution[1]) current.rows.push({ body: solution[1], kind: "plain" });
      return;
    }

    const step = line.match(/^(step\s*\d+)\s*:\s*(.*)$/i);
    if (step) {
      inSolution = true;
      inNote = false;
      current.rows.push({ label: step[1], body: step[2], kind: "step" });
      return;
    }

    const given = line.match(/^(given|required|let)\s*:\s*(.*)$/i);
    if (given) {
      inSolution = true;
      inNote = false;
      current.rows.push({ label: given[1], body: given[2], kind: "given" });
      return;
    }

    // The answer line. "∴" is how it is written in a CAPS answer book, and it
    // is the line a learner scans for, so it gets the margin symbol either way.
    const answer = line.match(/^(∴|therefore)\s*:?\s*(.*)$/i);
    if (answer) {
      inSolution = true;
      inNote = false;
      current.rows.push({ label: "∴", body: answer[2], kind: "answer" });
      return;
    }

    // A note that wraps onto a second line keeps its tail. Without this the
    // remainder lands in the working, where it reads as an orphaned step
    // sitting above the note it belongs to.
    if (inNote) {
      notes[notes.length - 1] = `${notes[notes.length - 1]} ${line}`.trim();
      return;
    }

    // A bare line inside a solution is usually continuation. But if another
    // "Solution:" still lies ahead, this line opens the next question — start
    // a new part rather than filing a question away as a step.
    if (inSolution) {
      if (lines.slice(i + 1).some(isSolutionLine)) {
        parts.push(current);
        current = { problem: [line], rows: [] };
        inSolution = false;
      } else {
        current.rows.push({ body: line, kind: "plain" });
      }
      return;
    }

    current.problem.push(line);
  });

  parts.push(current);
  return { levelCode, parts: parts.filter((p) => p.problem.length || p.rows.length), notes };
}

export function WorkedExample({ content, index }: { content: string; index: number }) {
  const { levelCode, parts, notes } = parseWorkedExample(content);
  const level = levelCode ? LEVELS[levelCode] : undefined;

  return (
    <article className="worked-example">
      <header className="we-head">
        <span className="we-number">{index + 1}</span>
        {level && (
          <span className={`we-level we-level-${levelCode?.toLowerCase()}`}>{level}</span>
        )}
      </header>

      {parts.map((part, p) => (
        <div key={p} className="we-part">
          {part.problem.length > 0 && (
            <div className="we-problem">
              <MathText content={part.problem.join("\n")} />
            </div>
          )}

          {part.rows.length > 0 && (
            // With no step labels anywhere in this block there is nothing to
            // put in the margin, so it collapses rather than leaving a gutter
            // of dead space beside the working.
            <div
              className={
                part.rows.some((r) => r.label) ? "we-working" : "we-working we-working-unlabelled"
              }
            >
              {part.rows.map((row, i) => (
                <div key={i} className={`we-row we-row-${row.kind}`}>
                  <div className="we-label">{row.label ?? ""}</div>
                  <MathText className="we-body" content={row.body} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {notes.map((note, i) => (
        <p key={i} className="we-note">
          <span className="we-note-label">Note</span>
          <MathText className="we-note-body" content={note} />
        </p>
      ))}
    </article>
  );
}
